<template>
  <div class="loja-painel">
    <!-- Carregando: esqueleto com a FORMA do que vem, não um "carregando..." (UI-31). -->
    <template v-if="carregando">
      <FishSkeleton forma="bloco" altura="120px" />
      <FishGrid :total="8" sem-linha-vazia>
        <FishSkeleton v-for="n in 8" :key="n" forma="encaixe" />
      </FishGrid>
    </template>

    <EmptyState
      v-else-if="erro"
      icone="exclamation-triangle"
      :titulo="$t('shop.panel.errorTitle')"
      :explicacao="erro"
      :acao="$t('shop.panel.retry')"
      icone-da-acao="sync"
      @acao="$emit('recarregar')"
    />

    <EmptyState
      v-else-if="!itens.length"
      :icone="iconeVazio"
      :titulo="$t('shop.panel.emptyTitle')"
      :explicacao="$t('shop.panel.emptyText')"
    />

    <template v-else>
      <!--
        1. O CARD DE DETALHE — um item por vez.

        É o mecanismo central do D16: **só um item é descrito**. A grade mostra ícone e raridade; o
        texto mora aqui. Assim o tamanho da descrição para de multiplicar por catorze, que é o que
        transformava a loja num paredão.
      -->
      <FishFrame v-if="selecionado" class="loja-painel__detalhe" tom="fundo">
        <div class="loja-painel__cabecalho">
          <FishIcon :src="selecionado.image" :alt="selecionado.name" tamanho="enorme" />
          <div class="crescer">
            <p class="t-subtitulo loja-painel__nome">
              {{ selecionado.name }}
              <span v-if="pacote > 1" class="loja-painel__pacote t-numero">&times;{{ pacote }}</span>
            </p>
            <p class="t-legenda loja-painel__raridade" :class="`loja-painel__raridade--${raridadeDe(selecionado)}`">
              {{ $t(`rarity.${raridadeDe(selecionado)}`) }}
            </p>
            <!-- O que separa dois pacotes do MESMO item: quantos vêm, e quanto sai cada um. Sem o
                 preço por unidade, comparar 10 por 220 com 25 por 500 vira conta de cabeça. -->
            <p v-if="pacote > 1" class="t-legenda loja-painel__unidade">
              {{ $t('shop.panel.pack', { count: pacote, unit: precoPorUnidade.toLocaleString('pt-BR') }) }}
            </p>
          </div>
        </div>

        <!--
          QUEM VESTE ISTO, e em que slot (09/09/2026).

          Vem antes da descrição porque é a pergunta que decide a compra, e a descrição é sabor:
          "Para o mercenário que prefere o encontro curto" **soa** como quem usa e não é — a Lâmina
          Afiada é do mercenário, mas a Tesoura de Poda tem prosa igualmente sugestiva e é do
          botânico. Enquanto isto não estava na tela, escolher entre as cinco ferramentas de 900
          era adivinhar, e errar custava o ouro inteiro: o servidor só recusa na hora de equipar.
        -->
        <p v-if="paraQuem" class="t-legenda loja-painel__para-quem">
          <FishIcon nome="user" tamanho="miudo" />
          {{ paraQuem }}
          <span v-if="selecionado.slot" class="loja-painel__slot">
            · {{ $t(`exploration.slots.${selecionado.slot}`) }}
          </span>
        </p>

        <p v-if="selecionado.description" class="t-corpo loja-painel__descricao">
          {{ selecionado.description }}
        </p>

        <!-- O que a poção faz. Fica fora dos atributos porque não é par rótulo/número: "levanta o
             ferimento" não tem valor para pôr na coluna da direita. -->
        <p v-if="efeitoDe(selecionado)" class="t-corpo loja-painel__efeito">
          {{ efeitoDe(selecionado) }}
        </p>

        <dl v-if="atributosDe(selecionado).length" class="loja-painel__atributos">
          <div v-for="a in atributosDe(selecionado)" :key="a.rotulo" class="loja-painel__atributo">
            <dt class="t-legenda">{{ a.rotulo }}</dt>
            <dd class="t-numero">{{ a.valor }}</dd>
          </div>
        </dl>

        <!--
          O que o item FAZ, nas palavras do catálogo (`tooltip_text`). Era o tooltip do `ItemCard`
          antigo; a grade perdeu o hover e este texto sumiu junto — pedido do autor em 07/09/2026.
        -->
        <ul v-if="linhasDaDica(selecionado).length" class="loja-painel__dica">
          <li v-for="(linha, i) in linhasDaDica(selecionado)" :key="i" class="t-legenda">{{ linha }}</li>
        </ul>

        <!--
          A tabela do baú: cada prêmio com a chance, do servidor — a mesma lista que o sorteio usa.
          É o que o jogador olha antes de gastar parallelium num baú, e não pode faltar.
        -->
        <div v-if="selecionado.rewards && selecionado.rewards.length" class="loja-painel__bau">
          <p class="t-legenda loja-painel__bau-titulo">{{ $t('shop.lootbox.possibleRewards') }}</p>
          <ul class="loja-painel__premios">
            <li v-for="(r, i) in selecionado.rewards" :key="i" class="loja-painel__premio">
              <span class="loja-painel__premio-nome">{{ nomeDoPremio(r) }}</span>
              <span class="t-numero loja-painel__premio-chance">{{ formatarChance(r.chance) }}%</span>
            </li>
          </ul>
        </div>

        <!-- A quantidade só existe para o que empilha (isca). Vara e barco são unidades.
             O campo é DIGITÁVEL (pedido do autor, 02/09): quem quer 200 iscas digita 200, em vez
             de clicar duzentas vezes. As setas continuam para o ajuste fino. -->
        <div v-if="selecionado.stackable" class="loja-painel__quantidade">
          <FishButton tamanho="pequeno" icone="chevron-left" som="" :desabilitado="quantidade <= 1" @click="quantidade--">
            <span class="so-leitor">{{ $t('shop.panel.less') }}</span>
          </FishButton>
          <input
            class="t-numero loja-painel__contador"
            type="number"
            inputmode="numeric"
            min="1"
            max="999"
            :value="quantidade"
            :aria-label="$t('shop.panel.buy')"
            @input="digitarQuantidade($event.target.value)"
            @blur="quantidade = quantidade || 1"
          >
          <FishButton tamanho="pequeno" icone="chevron-right" som="" :desabilitado="quantidade >= 999" @click="quantidade++">
            <span class="so-leitor">{{ $t('shop.panel.more') }}</span>
          </FishButton>
        </div>
      </FishFrame>

      <!--
        2. A GRADE DE ENCAIXES — todo o resto.

        Grade se lê de relance; card com parágrafo, não. E o requisito mora **no próprio encaixe**
        (5.4), em vez de um card inteiro desabilitado ocupando espaço na lista.
      -->
      <div class="loja-painel__grade">
        <FishGrid :total="itens.length" :colunas-telefone="4" :colunas-desktop="6" sem-linha-vazia>
          <FishSlot
            v-for="item in itens"
            :key="chaveDe(item)"
            :item="{
              id: item.id,
              nome: item.name,
              imagem: item.image,
              icone: item.icone,
              raridade: raridadeDe(item),
            }"
            :quantidade="pacoteDe(item)"
            multiplicador
            :selecionado="selecionado && chaveDe(selecionado) === chaveDe(item)"
            :requisito="requisitoDe(item)"
            :marca="marcaDe(item)"
            :dica="dicaDe(item)"
            @selecionar="selecionar(item)"
          />
        </FishGrid>
      </div>
    </template>

    <!--
      3. A AÇÃO, NA BASE (D11).

      "A ação principal de cada tela mora sempre no mesmo lugar — base da tela, ao alcance do
      polegar." Ela fica `sticky`, então continua alcançável mesmo com a grade rolada.

      O botão diz o PREÇO antes do clique, e o motivo quando não dá — o D11 manda o bloqueio dizer
      o que falta, não só que está bloqueado.
    -->
    <div v-if="selecionado" class="acao-principal loja-painel__acao">
      <FishButton
        variante="primario"
        tamanho="grande"
        bloco
        som="compra"
        :desabilitado="!podeComprar"
        :motivo="motivoDoBloqueio"
        @click="$emit('comprar', { item: selecionado, quantidade, total: precoTotal, unidades: quantidade * pacote })"
        @bloqueado="$emit('bloqueado', motivoDoBloqueio)"
      >
        {{ $t('shop.panel.buy') }} · {{ precoTotal.toLocaleString('pt-BR') }}
        <FishIcon :nome="moedaIcone" tamanho="miudo" />
      </FishButton>

      <!--
        O preço já vem do servidor com o evento aplicado; este selo só diz de onde veio a
        diferença. Sem ele o jogador vê a vara mais cara e não tem como saber por quê — e o
        calendário, que anuncia o evento, está três telas longe daqui.
      -->
      <p v-if="efeitoDePreco" class="t-legenda loja-painel__evento">
        {{ efeitoDePreco > 0 ? '▲' : '▼' }}
        {{ $t('shop.panel.priceEvent', { percent: Math.abs(efeitoDePreco) }) }}
      </p>
    </div>
  </div>
</template>

<script>
import { FishFrame, FishButton, FishGrid, FishSlot, FishIcon, FishSkeleton, EmptyState } from '@/shared/ui';
import { GENERO_DA_CATEGORIA, raridadePt } from '@/shared/utils/raridade';
import { marcaDeGuilda, paraQuemEmTexto } from '@/shared/utils/paraQuem';

/**
 * Onde `quantity` do catálogo premium significa **unidades**, e onde não significa.
 *
 * A coluna é uma só no banco, mas o `entregar()` do backend lê ela de três jeitos
 * (`premium.service.ts`): em `bait` e `tonic` são unidades que entram no inventário; em
 * `refrigeration` e `luxuryHotel` são **meses** (`quantidade * 30` dias), e em `tickets` é o
 * próprio produto — e nesses três o número já está escrito no nome ("6 meses de Frezzer", "5
 * tickets"). Escrever "×6" num Frezzer leria como seis geladeiras.
 *
 * Lista fechada, e não lista de exceções, de propósito: categoria nova aparece **sem** o multi-
 * plicador até alguém decidir o que o número dela quer dizer. Errar para menos aqui é deixar de
 * mostrar uma dica; errar para mais é mentir sobre o que o jogador leva.
 */
const CATEGORIAS_EM_UNIDADES = new Set(['bait', 'tonic', 'lootbox']);

/**
 * 5.3 e 5.4 — a metade direita da loja: **o painel** (D16).
 *
 * ## O que ele substitui
 *
 * Cada uma das seis lojas montava uma lista de `ItemCard`, e cada card repetia **ícone, nome,
 * descrição longa, dois atributos, preço, selo de nível e botão** — catorze de uma vez. O
 * diagnóstico do D16 é estrutural, não estético:
 *
 * > Sem ponto focal, sem sensação de lugar, e ler dá trabalho.
 *
 * ## As três camadas, e por que nessa ordem
 *
 * 1. **card de detalhe** do item selecionado — arte grande, nome, para que serve, atributos;
 * 2. **grade de encaixes** com todo o resto;
 * 3. **a ação**, na base.
 *
 * O detalhe vem **antes** da grade no DOM porque é o que responde "o que é isto"; a grade é
 * navegação. Num telefone, ler primeiro e escolher depois é a ordem natural — e é também a ordem
 * que o leitor de tela anuncia.
 *
 * ## Por que isto funciona e o paredão não
 *
 * **Só um item por vez é descrito.** A grade mostra ícone e raridade; o texto mora no card de
 * detalhe. Assim o tamanho da descrição **para de multiplicar por catorze** — que é o mecanismo
 * exato do paredão, e a razão de acrescentar um item à loja hoje piorar a tela.
 *
 * ## Um primitivo? Não — e é de propósito
 *
 * Ele importa de `@/shared/ui` mas **não** importa store nem service: recebe `itens` e emite
 * `comprar`. Fica em `shared/shop/` porque conhece o formato de item da loja (`name`, `price`,
 * `image`), que é conhecimento de tela. A regra do D4 vale para `shared/ui/`; aqui ele é reuso
 * entre as seis lojas, que é outra coisa.
 */
export default {
  name: 'LojaPainel',
  components: { FishFrame, FishButton, FishGrid, FishSlot, FishIcon, FishSkeleton, EmptyState },
  props: {
    /**
     * `[{ id, name, image, description?, price, rarity?, requiredLevel?, stackable?, chave?, icone? }]`
     *
     * `chave` identifica a linha quando o `id` **não** basta: o Posto de Expedição vende dois
     * catálogos (aeronave e item de integrante) que têm numeração própria, e sem ela clicar numa
     * aeronave acendia também o item de mesmo id. Loja de um catálogo só pode omitir.
     *
     * `icone` é o desenho de reserva do encaixe, para o item sem arte não virar um buraco.
     */
    itens: { type: Array, default: () => [] },
    carregando: { type: Boolean, default: false },
    erro: { type: String, default: '' },
    /** Quanto o jogador tem da moeda desta loja. */
    saldo: { type: Number, default: 0 },
    nivelDoJogador: { type: Number, default: 1 },
    iconeVazio: { type: String, default: 'nav-loja' },
    /** Ajuste de preço vindo de um evento ativo (D28): o efeito aparece **onde age**. */
    efeitoDePreco: { type: Number, default: 0 },
    /**
     * A moeda desta loja. A Premium vende por Parallelium, e mostrar o preço dela com a moedinha
     * de ouro fazia o valor parecer errado (pedido do autor, 02/09).
     */
    moeda: {
      type: String,
      default: 'ouro',
      validator: (v) => ['ouro', 'parallelium'].includes(v),
    },
  },
  emits: ['comprar', 'recarregar', 'selecionar', 'bloqueado'],
  data() {
    return { chaveSelecionada: null, quantidade: 1 };
  },
  computed: {
    selecionado() {
      return this.itens.find((i) => this.chaveDe(i) === this.chaveSelecionada) || null;
    },
    moedaIcone() {
      return this.moeda === 'parallelium' ? 'gem' : 'coins';
    },
    precoUnitario() {
      if (!this.selecionado) return 0;
      /*
       * O preço vem do servidor **já com o efeito de evento aplicado**, e é o mesmo número que o
       * caixa cobra — os dois passam por `precoDeLojaComEvento`, no domínio.
       *
       * Aqui havia uma segunda conta: `price * (1 + efeitoDePreco / 100)`, escrita quando o
       * servidor ignorava os eventos de preço e a tela era o único lugar onde eles apareciam.
       * Agora que o servidor cobra o aumento, refazer a conta aqui aplicaria o evento **duas
       * vezes** — a vitrine mostraria +44% num evento de +20% e o caixa cobraria +20%. Preço
       * exibido diferente do cobrado é exatamente o bug que o `precos.ts` existe para não deixar
       * voltar.
       *
       * O `efeitoDePreco` continua chegando porque é ele que explica o número: a intenção do D28
       * ("o efeito aparece onde ele age, não só numa lista") é atendida pelo preço já vir mudado,
       * e o selo ao lado diz por quê.
       */
      return Math.floor(Number(this.selecionado.price || 0));
    },
    precoTotal() {
      return this.precoUnitario * this.quantidade;
    },
    /** Quantas unidades vêm no item selecionado. 1 quando ele não é pacote. */
    pacote() {
      return this.pacoteDe(this.selecionado);
    },
    precoPorUnidade() {
      return Math.round(this.precoUnitario / this.pacote);
    },
    /**
     * "Só integrante Botânico" / "Qualquer integrante da squad" — e **vazio** fora da exploração.
     *
     * O vazio é o que deixa esta linha viver no painel das seis lojas: vara, isca, barco e casa
     * não têm guilda nem integrante, e `beneficiarioDe` devolve `null` para elas. Ver `paraQuem.js`.
     */
    paraQuem() {
      return this.paraQuemDe(this.selecionado);
    },
    nivelExigido() {
      return Number(this.selecionado?.requiredLevel || 0);
    },
    podeComprar() {
      if (!this.selecionado) return false;
      if (this.nivelExigido > this.nivelDoJogador) return false;
      return this.saldo >= this.precoTotal;
    },
    motivoDoBloqueio() {
      if (!this.selecionado || this.podeComprar) return '';
      if (this.nivelExigido > this.nivelDoJogador) {
        return this.$t('shop.panel.needLevel', { level: this.nivelExigido });
      }
      const falta = { amount: (this.precoTotal - this.saldo).toLocaleString('pt-BR') };
      return this.moeda === 'parallelium'
        ? this.$t('shop.panel.needParallelium', falta)
        : this.$t('shop.panel.needGold', falta);
    },
  },
  watch: {
    /**
     * Selecionar o primeiro assim que a lista chega.
     *
     * Sem isso o painel abre com o card de detalhe vazio e a ação some — e uma loja que abre sem
     * nada para comprar parece quebrada. O primeiro item é uma escolha honesta: é o mais barato na
     * ordem em que o backend devolve.
     */
    itens: {
      immediate: true,
      handler(lista) {
        if (!lista.length) {
          this.chaveSelecionada = null;
          return;
        }
        if (!lista.some((i) => this.chaveDe(i) === this.chaveSelecionada)) this.selecionar(lista[0]);
      },
    },
  },
  methods: {
    /** Aceita o que for digitado e prende em [1, 999]; vazio vira 1 no blur. */
    digitarQuantidade(valor) {
      const n = parseInt(valor, 10);
      if (Number.isNaN(n)) {
        this.quantidade = 0; // deixa o campo esvaziar enquanto digita; o blur volta para 1
        return;
      }
      this.quantidade = Math.min(999, Math.max(1, n));
    },
    /** Quem identifica a linha: a `chave` quando ela existe, o `id` nas lojas de um catálogo só. */
    chaveDe(item) {
      return item?.chave ?? item?.id;
    },
    selecionar(item) {
      this.chaveSelecionada = this.chaveDe(item);
      // A quantidade volta a 1 a cada troca: manter "12" ao trocar de isca para uma vara cara é o
      // tipo de estado grudento que faz alguém gastar sem querer.
      this.quantidade = 1;
      this.$emit('selecionar', item);
    },
    /**
     * A raridade em português, com a concordância da prateleira.
     *
     * O catálogo devolve inglês (`rods.rarity` é enum `common … mythic`); o aro do `FishSlot` e
     * as chaves de `rarity.*` são em português. Sem traduzir, o aro fica cinza para tudo e o
     * rótulo sai cru — a Marina mostrava **"rarity.epic"** na tela até 09/09/2026.
     */
    raridadeDe(item) {
      return raridadePt(item?.rarity || item?.raridade, GENERO_DA_CATEGORIA[item?.type] ?? 'f');
    },
    /**
     * O tamanho do pacote — o que estava faltando na loja premium.
     *
     * O catálogo vende o **mesmo** item em cinco embalagens: I.S.C.A 3000 por 15, 60, 110, 270 e
     * 520, que são 1, 5, 10, 25 e 50 iscas. Mesmo nome, mesma arte, mesma raridade — na grade eram
     * cinco encaixes idênticos com preços diferentes, e a loja parecia quebrada.
     */
    pacoteDe(item) {
      if (!item) return 1;
      const n = Number(item.quantity || 0);
      if (!Number.isFinite(n) || n <= 1) return 1;
      return CATEGORIAS_EM_UNIDADES.has(item.type) ? n : 1;
    },

    /**
     * O `tooltip_text` do catálogo, quebrado por `|` — era assim que o Strapi separava linhas, e
     * o painel continua escrevendo desse jeito.
     *
     * **Some no baú.** O texto dele é uma tabela de chances DIGITADA À MÃO na época do Strapi
     * ("50% Minhoca | 30% Moedas"), e ela não bate com o sorteio de verdade — que é 48% e 24%.
     * Com as duas na tela o jogador lia números diferentes para a mesma coisa, um deles falso
     * (visto no navegador, 09/09/2026). Quem manda é a tabela calculada pelo servidor, a mesma
     * que a caixa usa; a prosa antiga é ruído que envelheceu.
     */
    linhasDaDica(item) {
      if (item?.rewards?.length) return [];
      return String(item?.tooltipText || '')
        .split('|')
        .map((l) => l.trim())
        .filter(Boolean);
    },
    nomeDoPremio(r) {
      const quantidade =
        r.amount && typeof r.amount === 'object'
          ? `${r.amount.min}–${r.amount.max}`
          : r.amount && r.amount !== 1
            ? `${r.amount}`
            : '';
      const nome =
        r.type === 'gold' ? this.$t('shop.panel.rewardGold') : r.name || r.type;
      return quantidade ? `${quantidade}× ${nome}` : nome;
    },
    formatarChance(chance) {
      const n = Number(chance) || 0;
      return Number.isInteger(n) ? String(n) : n.toFixed(1);
    },
    /**
     * O bilhete do hover: nome, raridade, o que o item faz e, num baú, os prêmios com a chance de
     * cada um. Curto de propósito — quem quer o texto inteiro seleciona o encaixe e lê o detalhe.
     *
     * Sai **estruturado**, e não como frases prontas: quem desenha é o `FishSlot`, que alinha as
     * porcentagens em coluna e põe a raridade na cor dela. Frase pronta não se alinha.
     */
    dicaDe(item) {
      const pacote = this.pacoteDe(item);
      /*
       * A raridade só aparece quando o item TEM uma.
       *
       * `raridadeDe` cai em "comum" para quem não declara — o que serve ao aro do encaixe, que
       * precisa de alguma cor. No bilhete isso vira ruído: o catálogo premium não tem raridade,
       * e escrever "Comum" embaixo de cada tônico e de cada baú é uma linha que não informa nada.
       */
      const raridade = item.rarity || item.raridade ? this.raridadeDe(item) : '';
      /*
       * Quem veste, e o efeito da poção — as duas linhas que o bilhete não tinha.
       *
       * Vão **na frente** da prosa do catálogo, e não atrás: o bilhete corta em três frases, e a
       * descrição de sabor empurraria para fora justamente o que decide a compra. É a mesma ordem
       * do card de detalhe, e as duas telas lidas na mesma ordem é o que faz a grade e o detalhe
       * parecerem a mesma coisa em dois tamanhos.
       */
      const cabecalho = [this.paraQuemDe(item), this.efeitoDe(item)].filter(Boolean);
      return {
        titulo: item.name,
        raridade,
        raridadeRotulo: raridade ? this.$t(`rarity.${raridade}`) : '',
        legenda:
          pacote > 1
            ? this.$t('shop.panel.pack', {
                count: pacote,
                unit: Math.round(Number(item.price || 0) / pacote).toLocaleString('pt-BR'),
              })
            : item.slot
              ? this.$t(`exploration.slots.${item.slot}`)
              : '',
        // Três frases no máximo: o bilhete é um resumo, e o detalhe fica no card ao lado.
        texto: [...cabecalho, ...this.linhasDaDica(item)].slice(0, 3),
        atributos: this.atributosDe(item),
        premiosTitulo: this.$t('shop.lootbox.possibleRewards'),
        premios: (item.rewards || []).map((r) => ({
          chance: this.formatarChance(r.chance),
          nome: this.nomeDoPremio(r),
        })),
      };
    },
    /**
     * O tradutor, como função solta, para `paraQuem.js`.
     *
     * `this.$t` não vai direto por parâmetro: passar o método desnudo perde o `this` do composer
     * do vue-i18n, e o sintoma é o pior — a chave sai crua na tela em vez de estourar.
     */
    traduzir(chave, parametros) {
      return this.$t(chave, parametros);
    },
    /** O requisito, curto o bastante para caber no encaixe (5.4). */
    requisitoDe(item) {
      const nivel = Number(item.requiredLevel || 0);
      return nivel > this.nivelDoJogador ? `Nv. ${nivel}` : '';
    },
    /**
     * A guilda na tarja do encaixe — a marca que a grade não tinha.
     *
     * Separada de `requisitoDe` de propósito. `requisito` significa **bloqueado**: acinzenta a
     * arte e o leitor de tela anuncia "bloqueado: Nv. 12". A ferramenta de guilda não está
     * bloqueada — dá para comprar, e é boa; ela só serve a um dos cinco. Marcar as duas coisas
     * com a mesma tarja diria que metade da prateleira está fora de alcance.
     */
    marcaDe(item) {
      return marcaDeGuilda(item, this.traduzir);
    },
    /** "Só integrante Botânico" — e vazio fora da exploração. Ver `paraQuem.js`. */
    paraQuemDe(item) {
      return paraQuemEmTexto(item, this.traduzir);
    },
    /** O que a poção faz, na mesma frase que a oficina usa. Vazio no equipamento. */
    efeitoDe(item) {
      const efeito = item?.efeito;
      if (!efeito) return '';
      const partes = [];
      if (efeito.curaHp > 0) {
        partes.push(this.$t('exploration.explorers.effects.curaHp', { valor: efeito.curaHp }));
      }
      if (efeito.restauraStamina > 0) {
        partes.push(
          this.$t('exploration.explorers.effects.restauraStamina', { valor: efeito.restauraStamina }),
        );
      }
      if (efeito.curaFerimento) partes.push(this.$t('exploration.explorers.effects.curaFerimento'));
      return partes.join(' · ');
    },
    /**
     * Os atributos que este item tem, e só eles.
     *
     * Uma vara tem poder e durabilidade; um barco tem velocidade; uma isca não tem nenhum. Listar
     * campos vazios com "—" é o que fazia o card ter altura fixa para o pior caso — e altura fixa
     * com texto é o UI-17.
     */
    atributosDe(item) {
      const mapa = [
        ['power', this.$t('shop.panel.power')],
        // O poder da ISCA — o único número que ela tem, e que não aparecia em lugar nenhum da
        // loja: o servidor manda `poderDePesca`, o adapter guarda em `fishingPower`, e esta
        // lista não o citava. A Casa de Iscas vendia três iscas sem dizer no que diferem.
        // Mesmo rótulo de `power`: os dois nunca aparecem no mesmo item.
        ['fishingPower', this.$t('shop.panel.power')],
        ['durability', this.$t('shop.panel.durability')],
        ['speed', this.$t('shop.panel.speed')],
        ['rechargeStamina', this.$t('shop.panel.recharge')],
        ['catchRate', this.$t('shop.panel.catchRate')],
        // As duas medidas da aeronave. Sao o que separa um modelo do seguinte —
        // quantos integrantes a squad leva e ate que bioma ela chega —, e o
        // Posto vendia sete avioes sem dizer nenhuma das duas.
        ['capacidade', this.$t('shop.panel.seats')],
        ['alcanceNome', this.$t('shop.panel.reach')],
      ];
      const atributos = mapa
        .filter(([chave]) => item[chave] != null && item[chave] !== '')
        .map(([chave, rotulo]) => ({ rotulo, valor: item[chave] }));

      /*
       * Os stats do item de exploração (09/09/2026).
       *
       * Vêm de `bonus`, um objeto com **só o que não é zero** (`{ carga: 1, sorte: 1 }`), e não de
       * colunas fixas — por isso não cabem no mapa acima, que é chave a chave. O Posto vendia doze
       * itens sem dizer o que nenhum deles somava: quatro deles diferem apenas nisso.
       *
       * O `+` no valor é o que separa "Carga 1" de "Carga +1". O primeiro lê como o atributo do
       * item; o segundo, como o que ele acrescenta ao integrante — e é o segundo que é verdade.
       */
      for (const [stat, valor] of Object.entries(item.bonus || {})) {
        if (valor) atributos.push({ rotulo: this.$t(`exploration.stats.${stat}`), valor: `+${valor}` });
      }
      if (item.defesa > 0) {
        atributos.push({ rotulo: this.$t('exploration.stats.defesa'), valor: `+${item.defesa}` });
      }
      return atributos;
    },
  },
};
</script>

<style scoped>
.loja-painel {
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
  min-height: 0;
  /* TAMANHO ÚNICO nas seis lojas (pedido do autor, 02/09): o painel não cresce com o estoque — a
   * Premium tem 27 itens e esticava o modal além da moldura. A grade rola por dentro. */
  height: clamp(420px, 62vh, 640px);
}

.loja-painel__detalhe {
  flex-shrink: 0;
}

.loja-painel__dica {
  margin: var(--e-2) 0 0;
  padding: 0 0 0 var(--e-3);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.loja-painel__bau {
  margin-top: var(--e-2);
}

.loja-painel__bau-titulo {
  margin: 0 0 var(--e-1);
}

.loja-painel__premios {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 2px var(--e-3);
}

.loja-painel__premio {
  display: flex;
  justify-content: space-between;
  gap: var(--e-2);
  font-size: var(--f-2);
}

.loja-painel__premio-chance {
  color: var(--texto-fraco);
}

/* A grade é a única parte que rola; o detalhe fica em cima e a ação embaixo, sempre visíveis. */
.loja-painel__grade {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* os aros de raridade desenham 2px PARA FORA do encaixe — sem esta folga o overflow os corta */
  padding: 3px;
}

.loja-painel__acao {
  flex-shrink: 0;
}

/* O selo do evento de preço: explica a diferença sem competir com o botão.
   Tamanho e cor vêm da classe , que é o que o resto deste arquivo usa. */
/* O selo do evento de preço: explica a diferença sem competir com o botão.
   Tamanho e cor vêm da classe `.t-legenda`, que é o que o resto deste arquivo usa —
   `--t-*` aqui são durações de transição, não escala tipográfica. */
.loja-painel__evento {
  margin: var(--e-1) 0 0;
  text-align: center;
}

.loja-painel__cabecalho {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  margin-bottom: var(--e-2);
}

/* O retrato do item no detalhe: 96px (pedido do autor — os 64 deixavam o produto pequeno demais
 * para ser a "arte grande" que o D16 promete). */
.loja-painel__cabecalho .fish-icon {
  width: 96px;
  height: 96px;
}

.loja-painel__nome {
  margin: 0;
  color: var(--tinta);
}

.loja-painel__raridade {
  margin: 0;
  font-family: var(--fonte-display);
}

/* O ×N ao lado do nome: é o mesmo número do encaixe, no mesmo registro, para que a grade e o
 * detalhe se leiam como uma coisa só. */
.loja-painel__pacote {
  color: var(--ouro-claro);
  font-size: var(--f-3);
  margin-left: var(--e-2);
}

.loja-painel__unidade {
  margin: 0;
  color: var(--tinta-fraca);
}

.loja-painel__raridade--comum { color: var(--raridade-comum); }
.loja-painel__raridade--incomum { color: var(--raridade-incomum); }
.loja-painel__raridade--raro,
.loja-painel__raridade--rara { color: var(--raridade-rara); }
.loja-painel__raridade--epico,
.loja-painel__raridade--epica { color: var(--raridade-epica); }
.loja-painel__raridade--lendario,
.loja-painel__raridade--lendaria { color: var(--raridade-lendaria); }
.loja-painel__raridade--mitico,
.loja-painel__raridade--mitica { color: var(--raridade-mitica); }

.loja-painel__descricao {
  color: var(--tinta-fraca);
  margin: 0 0 var(--e-3);
}

/*
 * "Para · Só integrante Botânico".
 *
 * Em `--tinta`, e não na `--tinta-fraca` da descrição: é o único texto do card que muda o que se
 * compra, e ficaria abaixo da prosa de sabor na hierarquia se tivesse a mesma cor dela. O ícone
 * alinhado pelo `baseline` porque em `center` ele sobe meio pixel sobre a maiúscula e a linha
 * parece torta em 12px.
 */
.loja-painel__para-quem {
  display: flex;
  align-items: baseline;
  gap: var(--e-1);
  margin: 0 0 var(--e-1);
  color: var(--tinta);
  font-family: var(--fonte-display);
}

.loja-painel__slot {
  color: var(--tinta-fraca);
}

.loja-painel__efeito {
  color: var(--tinta);
  margin: 0 0 var(--e-3);
}

.loja-painel__atributos {
  display: flex;
  flex-wrap: wrap;
  gap: var(--e-2) var(--e-5);
  margin: 0;
}

.loja-painel__atributo {
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
}

.loja-painel__atributo dt {
  color: var(--tinta-fraca);
}

.loja-painel__atributo dd {
  margin: 0;
  color: var(--tinta);
  font-size: var(--f-4);
}

.loja-painel__quantidade {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--e-3);
  margin-top: var(--e-3);
}

/* O contador virou um campo: digita-se 200 em vez de clicar 200 vezes. As setinhas nativas do
 * `type=number` saem — as flechas do jogo já fazem esse papel, e as nativas destoam do pixel. */
.loja-painel__contador {
  font-size: var(--f-4);
  font-family: var(--fonte-display);
  color: var(--tinta);
  width: 5ch;
  text-align: center;
  background: var(--papel-luz);
  border: 2px solid var(--papel-sombra);
  padding: var(--e-1) 0;
  -moz-appearance: textfield;
  appearance: textfield;
}

.loja-painel__contador::-webkit-outer-spin-button,
.loja-painel__contador::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* A ação gruda na base do painel, não da janela: a loja vive dentro de um modal, e `sticky` contra
 * o contêiner que rola é o que mantém o botão visível com a grade rolada. */
.loja-painel__acao {
  background: none;
  padding: var(--e-2) 0 0;
}

/*
 * NO TELEFONE O DETALHE CEDE ALTURA À GRADE (28/09/2026).
 *
 * O painel tem altura travada (regra 3 do DESIGN-SYSTEM §11) e o detalhe não encolhia: num celular
 * de 360×780 o `62vh` dá ~480px, o detalhe de um item com retrato de 96px, "para quem", descrição,
 * atributos e seletor de quantidade comia ~300, o botão ~70 — e a grade ficava com uma faixa de
 * linha e meia, a segunda fileira de encaixes cortada ao meio. Relatado como "está espremendo pra
 * caber".
 *
 * Abaixo de 600px o detalhe tem TETO (45% do painel) e rola por dentro quando passa dele, e a grade
 * tem PISO (duas fileiras de encaixe). O retrato desce para 64px, o `enorme` do `FishIcon` — os 96
 * servem ao desktop, onde o painel chega a 640px. Fica no FIM do arquivo porque as regras de mesma
 * especificidade acima (o retrato de 96px) ganhariam de uma media query escrita antes delas.
 */
@media (max-width: 599px) {
  /* A altura travada continua (o `NpcStage` não pode esticar), mas no telefone ela é a da janela
   * ÚTIL — entre o cabeçalho e a barra —, e não 62% da janela inteira: em 360×780 são ~600px de
   * painel onde eram 480, e a loja rolada até o topo ocupa a tela sem sobrar nem faltar. O `vh` é o
   * fallback do navegador sem `dvh`. */
  .loja-painel {
    height: clamp(420px, calc(100vh - var(--altura-cabecalho) - var(--altura-barra) - var(--e-4)), 640px);
    height: clamp(420px, calc(100dvh - var(--altura-cabecalho) - var(--altura-barra) - var(--e-4)), 640px);
  }

  .loja-painel__detalhe {
    flex-shrink: 1;
    min-height: 0;
    max-height: 45%;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .loja-painel__grade {
    min-height: 168px;
  }

  .loja-painel__cabecalho .fish-icon {
    width: 64px;
    height: 64px;
  }

  .loja-painel__descricao {
    margin-bottom: var(--e-2);
  }

  .loja-painel__quantidade {
    margin-top: var(--e-2);
  }
}
</style>
